import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Sparkles, Zap, Search, FileText, Hash, ShieldCheck, ArrowRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";

const features = [
    { Icon: Search, tKey: "aiPage.features.buyerFinder.title", dKey: "aiPage.features.buyerFinder.description" },
    { Icon: Zap, tKey: "aiPage.features.supplierFinder.title", dKey: "aiPage.features.supplierFinder.description" },
    { Icon: FileText, tKey: "aiPage.features.quotation.title", dKey: "aiPage.features.quotation.description" },
    { Icon: Hash, tKey: "aiPage.features.hsCode.title", dKey: "aiPage.features.hsCode.description" },
    { Icon: ShieldCheck, tKey: "aiPage.features.compliance.title", dKey: "aiPage.features.compliance.description" },
];

export default function ExportfyAI() {
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", email: "", company: "", interested_features: [] });
    const [loading, setLoading] = useState(false);
    const toggle = (f) => {
        setForm((s) => ({
            ...s,
            interested_features: s.interested_features.includes(f)
                ? s.interested_features.filter((x) => x !== f)
                : [...s.interested_features, f],
        }));
    };
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/waitlist", form);
            toast.success(t("toast.waitlistJoined"));
            setForm({ name: "", email: "", company: "", interested_features: [] });
        } catch (err) {
            if (err?.response?.status === 409) toast.info(t("toast.waitlistDuplicate"));
            else toast.error(t("toast.genericError"));
        } finally { setLoading(false); }
    };
    return (
        <>
            <SEO
                title="Exportfy AI — Trade Intelligence Platform (Coming Soon) | Exportfy.world"
                description="Exportfy AI is our in-development trade intelligence platform — AI Buyer Finder, AI Supplier Finder, AI Quotation Generator, AI HS Code Assistant, and AI Compliance Advisor. Join the waitlist."
                path="/exportfy-ai"
            />
            <PageHero
                eyebrow={t("aiPage.hero.eyebrow")}
                title={t("aiPage.hero.title")}
                subtitle={t("aiPage.hero.subtitle")}
                image="https://images.unsplash.com/photo-1620325867502-221cfb5faa5f?w=1600"
            />

            {/* Features */}
            <section className="section-y bg-[#061A2E] text-white relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none [background:radial-gradient(circle_at_25%_20%,rgba(16,185,129,0.14),transparent_40%),radial-gradient(circle_at_75%_60%,rgba(29,78,216,0.18),transparent_45%)]" />
                <div className="relative container-x">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                            <Sparkles className="w-3.5 h-3.5" /> {t("aiPage.banner")}
                        </div>
                        <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{t("aiPage.featuresTitle")}</h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map(({ Icon, tKey, dKey }, i) => (
                            <ScrollReveal key={tKey} delay={i * 50}>
                                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-sm h-full hover:bg-white/[0.06] transition">
                                    <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-300"><Icon className="w-5 h-5" strokeWidth={1.75} /></div>
                                    <div className="mt-5 font-bold text-lg text-white">{t(tKey)}</div>
                                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{t(dKey)}</p>
                                    <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-amber-300">{t("aiPage.featureStatus")}</div>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Waitlist */}
            <section className="section-y">
                <div className="container-x max-w-3xl">
                    <ScrollReveal>
                        <div className="text-center">
                            <div className="overline">{t("aiPage.waitlist.eyebrow")}</div>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A2540] tracking-tight">{t("aiPage.waitlist.title")}</h2>
                            <p className="mt-4 text-slate-500">{t("aiPage.waitlist.subtitle")}</p>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal delay={100}>
                        <form onSubmit={submit} className="mt-12 bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-900/5 p-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input placeholder={t("aiPage.waitlist.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.waitlist.name} />
                                <Input required type="email" placeholder={t("aiPage.waitlist.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.waitlist.email} />
                            </div>
                            <Input className="mt-4" placeholder={t("aiPage.waitlist.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.waitlist.company} />
                            <div className="mt-6">
                                <div className="font-bold text-[#0A2540] text-sm">{t("aiPage.waitlist.interested")}</div>
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {features.map((f) => (
                                        <label key={f.tKey} className="flex items-start gap-2 text-sm text-slate-700 rounded-xl border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer">
                                            <Checkbox checked={form.interested_features.includes(f.tKey)} onCheckedChange={() => toggle(f.tKey)} />
                                            <span>{t(f.tKey)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <Button type="submit" disabled={loading} data-testid={FORMS.waitlist.submit} className="mt-6 w-full rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold py-6">
                                {loading ? t("aiPage.waitlist.joining") : (<>{t("aiPage.waitlist.button")} <ArrowRight className="ml-2 w-4 h-4" /></>)}
                            </Button>
                        </form>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
